import { useMemo } from 'react';
import { Stack, Grid, Card, CardHeader, CardContent, Box, TextField, Button, Tooltip, Typography } from '@mui/material';
import { DataGridPro, GridColDef } from '@mui/x-data-grid-pro';
import { BaseRecord, CrudFilters, getDefaultFilter, useMany } from '@refinedev/core';
import { List, useDataGrid, EditButton, DeleteButton, DateField } from '@refinedev/mui';
import { useForm } from '@refinedev/react-hook-form';
import { Controller } from 'react-hook-form';

import { UnifiedMitigation, UnifiedMitigationType } from '../../types';

const UnifiedMitigationList = () => {
  const { dataGridProps, filters, search } = useDataGrid({
    onSearch: (params: { search: string }) => {
      const filters: CrudFilters = [];

      filters.push({
        field: 'search',
        operator: 'eq',
        value: params.search
      });

      return filters;
    },

    filters: {
      initial: [
        {
          field: 'search',
          operator: 'eq',
          value: ''
        }
      ]
    }
  });

  const mitigationTypeIds = dataGridProps.rows.map((item: UnifiedMitigation) => item.unifiedmitigationtype);

  const {
    result: mitigationTypes,
    query: { isLoading: isLoadingMitigationTypes }
  } = useMany<UnifiedMitigationType>({
    resource: 'unified_mitigation_types',
    ids: [...new Set(mitigationTypeIds)],
    queryOptions: {
      enabled: mitigationTypeIds.length > 0
    }
  });

  const columns = useMemo<GridColDef[]>(
    () => [
      {
        field: 'name',
        headerName: 'Name',
        width: 200,
        flex: 2,
        renderCell: params => {
          return (
            <Tooltip title={params.row.name}>
              <Typography variant="body1" noWrap>
                {params.row.name}
              </Typography>
            </Tooltip>
          );
        }
      },
      { field: 'description', headerName: 'Description', sortable: false, flex: 1 },
      {
        field: 'unifiedmitigationtype',
        headerName: 'Mitigation type',
        flex: 1,
        renderCell: ({ row }) => {
          if (isLoadingMitigationTypes) {
            return 'Loading...';
          }
          const mitigationType = mitigationTypes?.data.find(
            (item: BaseRecord) => item.id === row.unifiedmitigationtype
          );

          return mitigationType?.name;
        }
      },
      {
        field: 'created',
        headerName: 'Created At',
        renderCell: ({ value }) => <DateField format="LLL" value={value} />,
        flex: 1
      },
      {
        field: 'modified',
        headerName: 'Updated At',
        renderCell: ({ value }) => <DateField format="LLL" value={value} />,
        flex: 1
      },
      {
        field: 'actions',
        headerName: 'Actions',
        minWidth: 250,
        sortable: false,
        renderCell: params => {
          return (
            <Stack direction="row" spacing={1}>
              <EditButton hideText recordItemId={params.row.id} />
              <DeleteButton
                hideText
                recordItemId={params.row.id}
                confirmTitle={
                  'Do you want to delete Unified Mitigation: "' +
                  params.row.name +
                  '" ? This will also delete the "Legacy" Mitigation.'
                }
              />
            </Stack>
          );
        }
      }
    ],
    [isLoadingMitigationTypes, mitigationTypes]
  );

  const { control, handleSubmit } = useForm({
    defaultValues: {
      search: getDefaultFilter('search', filters, 'eq') || ''
    }
  });

  // Remove sorting until https://github.com/refinedev/refine/issues/7099 is fixed
  const { sortingMode, sortModel, onSortModelChange, ...restDataGridProps } = dataGridProps;

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} lg={3}>
        <Card>
          <CardHeader title="Filters" />
          <CardContent>
            <Box
              component="form"
              autoComplete="off"
              sx={{
                display: 'flex',
                flexDirection: 'column'
              }}
              onSubmit={handleSubmit(search)}
            >
              <Controller
                name="search"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <TextField
                    value={value || ''}
                    onChange={onChange}
                    label="Search"
                    placeholder="Search by name"
                    margin="normal"
                    fullWidth
                    autoFocus
                  />
                )}
              />

              <br />
              <Button type="submit" variant="contained">
                Apply
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} lg={9}>
        <List canCreate>
          <DataGridPro {...restDataGridProps} filterModel={undefined} disableColumnMenu columns={columns} pagination />
        </List>
      </Grid>
    </Grid>
  );
};

export default UnifiedMitigationList;
